import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { GeoProjection } from "d3-geo";
import type { FeatureCollection, Geometry } from "geojson";

export type MapboxStyle = 
  | 'outdoors-v12'
  | 'satellite-streets-v12'
  | 'streets-v12'
  | 'light-v11'
  | 'dark-v11'
  | 'satellite-v9'
  | 'navigation-day-v1';

type Props = {
  width: number;
  height: number;
  project: GeoProjection;
  stroke?: string;
  fill?: string;
  borderFC: FeatureCollection<Geometry> | null;
  adm2FC: FeatureCollection<Geometry> | null;
  zoom?: number;  // Mapbox zoom level (default 7.5)
  mapStyle?: MapboxStyle;  // User-selected map style
  onMapReady?: () => void;  // Callback when map is ready for positioning
  interactive?: boolean;  // Enable zoom/pan controls (default false)
  showBorder?: boolean;  // Show country border (default true)
};

export interface MapboxHandle {
  getMap: () => mapboxgl.Map | null;
  project: (lngLat: [number, number]) => { x: number; y: number } | null;
}

const LebanonMapBaseMapbox = forwardRef<MapboxHandle, Props>(({
  width,
  height,
  stroke = "#1976d2",
  fill = "rgba(25, 118, 210, 0.08)",
  borderFC,
  adm2FC,
  zoom = 7.5,
  mapStyle = 'streets-v12',
  onMapReady,
  interactive = false,
  showBorder = true
}, ref) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  // Expose map instance and projection function to parent
  useImperativeHandle(ref, () => ({
    getMap: () => mapRef.current,
    project: (lngLat: [number, number]) => {
      if (!mapRef.current) return null;
      const point = mapRef.current.project(lngLat);
      return { x: point.x, y: point.y };
    },
  }));

  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Clean up existing map if style changed
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    // Set Mapbox access token
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';

    // Initialize map with user-selected style
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: `mapbox://styles/mapbox/${mapStyle}`,
      center: [35.9, 33.9], // Lebanon center
      zoom: zoom,
      interactive: interactive,
      attributionControl: false,
    });

    // Add navigation controls if interactive
    if (interactive) {
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    }

    map.on('load', () => {
      // Add country border layer (only if showBorder is true)
      if (showBorder && borderFC) {
        map.addSource('lebanon-border', {
          type: 'geojson',
          data: borderFC as any,
        });

        map.addLayer({
          id: 'lebanon-border-line',
          type: 'line',
          source: 'lebanon-border',
          paint: {
            'line-color': stroke,
            'line-width': 2.5,
            'line-opacity': 1,
          },
        });

        // Add subtle highlight
        map.addLayer({
          id: 'lebanon-border-highlight',
          type: 'line',
          source: 'lebanon-border',
          paint: {
            'line-color': 'rgba(255,255,255,0.6)',
            'line-width': 1,
            'line-opacity': 0.8,
          },
        });
      }

      // Add administrative divisions layer
      if (adm2FC) {
        map.addSource('lebanon-adm2', {
          type: 'geojson',
          data: adm2FC as any,
        });

        map.addLayer({
          id: 'lebanon-adm2-fill',
          type: 'fill',
          source: 'lebanon-adm2',
          paint: {
            'fill-color': fill,
            'fill-opacity': 0.1,
          },
        });

        map.addLayer({
          id: 'lebanon-adm2-line',
          type: 'line',
          source: 'lebanon-adm2',
          paint: {
            'line-color': stroke,
            'line-width': 1.5,
            'line-opacity': 0.6,
          },
        });
      }
      
      // Notify parent that map is ready
      if (onMapReady) {
        onMapReady();
      }
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [borderFC, adm2FC, stroke, fill, zoom, mapStyle, interactive, showBorder]);

  return (
    <div
      ref={mapContainerRef}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        width: width,
        height: height,
      }}
    />
  );
});

LebanonMapBaseMapbox.displayName = 'LebanonMapBaseMapbox';

export default LebanonMapBaseMapbox;

